import React, { useEffect, useState } from 'react';
import { Box, Button, Snackbar, Typography } from '@mui/material';
import { colors, radii } from '../theme/colors';

const STORAGE_KEY = 'consent_choice';

/**
 * Cookie / pixel consent banner. Renders nothing unless the
 * `REACT_APP_REQUIRE_CONSENT` env var is the literal string `'true'`.
 *
 * Behavior:
 * - Shows once on first visit (no `localStorage.consent_choice` yet).
 * - On Accept: stores `'granted'` and pushes a Google Consent Mode v2
 *   `consent_update` event to `window.dataLayer` with `ad_storage` and
 *   `analytics_storage` both granted. GTM tags configured for consent
 *   gating begin firing.
 * - On Reject: stores `'denied'` and pushes the same event with both
 *   set to `'denied'`. GTM tags should NOT fire (configured per-tag).
 *
 * The app's `trackEvent()` calls themselves are NOT gated — they always
 * push to dataLayer. Gating happens at the GTM tag firing layer so the
 * application stays consent-agnostic.
 *
 * Default state (`REACT_APP_REQUIRE_CONSENT=false`): banner never renders,
 * no consent signal is pushed, GTM tags fire normally (US-only default).
 * The marketing agency flips the env var to `'true'` for EU launches.
 *
 * Takes no props. Mount once near the app root (in App.js).
 */
export default function ConsentBanner() {
  const required = process.env.REACT_APP_REQUIRE_CONSENT === 'true';
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!required) return;
    try {
      const existing = window.localStorage.getItem(STORAGE_KEY);
      if (!existing) setOpen(true);
    } catch (_) {
      // localStorage may be blocked in private modes; default to showing the banner
      setOpen(true);
    }
  }, [required]);

  if (!required) return null;

  const recordChoice = (granted) => {
    try { window.localStorage.setItem(STORAGE_KEY, granted ? 'granted' : 'denied'); } catch (_) {}
    if (typeof window !== 'undefined' && Array.isArray(window.dataLayer)) {
      window.dataLayer.push({
        event: 'consent_update',
        ad_storage: granted ? 'granted' : 'denied',
        analytics_storage: granted ? 'granted' : 'denied',
      });
    }
    setOpen(false);
  };

  return (
    <Snackbar
      open={open}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      ContentProps={{
        sx: {
          bgcolor: colors.surface,
          color: colors.textPrimary,
          border: `1px solid ${colors.borderSubtle}`,
          borderRadius: `${radii.panel}px`,
          maxWidth: 520,
        },
      }}
      message={
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Typography variant="body2" sx={{ color: colors.textPrimary }}>
            We use cookies and similar technologies to analyze traffic and improve our service.
            See our <a href="/privacy" style={{ color: colors.seal }}>privacy policy</a> for details.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button size="small" variant="outlined" onClick={() => recordChoice(false)} sx={{ color: colors.textPrimary, borderColor: colors.borderEmphasis }}>
              Reject
            </Button>
            <Button size="small" variant="contained" onClick={() => recordChoice(true)}>
              Accept
            </Button>
          </Box>
        </Box>
      }
    />
  );
}
